import React, { useEffect, useState } from 'react'
import { useTabAuth } from '@/hooks/useTabAuth'
import { UserInfo } from '@/api/types/user'
import { useUserProfile } from '@/hooks/user/useUserProfile'
import { useFileMedia } from '@/hooks/useFileMedia'
import icQr from '@/assets/images/ic_qr.png'
import QrCodeDialog from './QrCodeDialog'
import ToastUtils from '@/utils/toast-utils'
import { useAuth } from '@/hooks/useAuth'
import { logPrint } from '@/utils/my-utils'

// 编辑时的临时状态
interface EditFormState {
  nickname: string
  gender: 'male' | 'female' | 'unknown'
  bio: string
  avatarFile: File | null
  avatarPreview: string | null
}

export default function ProfileSettings(): React.JSX.Element {
  const tabAuth = useTabAuth()
  const { updateUserInfo, isLoading: profileLoading, error: profileError, clearError } = useUserProfile()
  const { userLogout } = useAuth()
  const { uploadAvatar, selectFile, getFilePreview, isLoading: uploadLoading, error: uploadError, clearError: clearUploadError } = useFileMedia()

  // 基础状态
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [showQrDialog, setShowQrDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)

  // 编辑表单状态
  const [editForm, setEditForm] = useState<EditFormState>({
    nickname: '',
    gender: 'unknown',
    bio: '',
    avatarFile: null,
    avatarPreview: null
  })

  const loading = isLoading || profileLoading || uploadLoading
  const currentError = profileError || uploadError

  // 加载用户数据
  useEffect(() => {
    if (tabAuth.isReady) {
      const userData = tabAuth.getUserInfo()
      setIsLoading(false)

      // 初始化编辑表单
      if (userData) {
        setUserInfo(userData)
        setEditForm({
          nickname: userData.nickname || '',
          gender: userData.gender || 'unknown',
          bio: userData.bio || '',
          avatarFile: null,
          avatarPreview: null
        })
      }
    }
  }, [tabAuth.isReady])

  // 二维码弹框控制
  const handleShowQrCode = () => setShowQrDialog(true)
  const handleCloseQrDialog = () => setShowQrDialog(false)

  // 开始编辑
  const handleStartEdit = () => {
    setIsEditing(true)
  }

  // 取消编辑
  const handleCancelEdit = () => {
    // 恢复原始数据
    if (userInfo) {
      setEditForm({
        nickname: userInfo.nickname || '',
        gender: userInfo.gender || 'unknown',
        bio: userInfo.bio || '',
        avatarFile: null,
        avatarPreview: null
      })
    }
    setIsEditing(false)
    clearError()
    clearUploadError()
  }

  // 更新编辑表单字段
  const updateEditForm = (updates: Partial<EditFormState>) => {
    setEditForm(prev => ({ ...prev, ...updates }))
  }

  // 选择头像文件（使用统一的文件选择器）
  const handleAvatarSelect = async () => {
    try {
      const file = await selectFile({
        type: 'image',
        title: '选择头像图片'
      })

      if (!file) {
        return // 用户取消选择
      }

      // 验证文件大小 (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('图片文件不能超过5MB')
        return
      }

      logPrint("file", file)

      // 获取文件预览
      const previewUrl = await getFilePreview(file)

      updateEditForm({
        avatarFile: file,
        avatarPreview: previewUrl
      })
    } catch (error) {
      console.error('选择头像失败:', error)
      alert('选择头像失败，请重试')
    }
  }

  // 点击头像触发文件选择
  const handleAvatarClick = () => {
    if (isEditing) {
      handleAvatarSelect() // 直接调用统一的文件选择方法
    }
  }

  // 保存编辑
  const handleSaveEdit = async () => {
    try {
      ToastUtils.showLoading(true)

      let avatarUrl = userInfo?.avatar || ''

      // 如果有新头像文件，先上传
      if (editForm.avatarFile) {
        const uploadResult = await uploadAvatar(editForm.avatarFile)
        if (!uploadResult) {
          alert('头像上传失败，请重试')
          return
        }
        avatarUrl = uploadResult.accessUrl
      }

      // 更新用户信息
      const updateResult = await updateUserInfo({
        nickname: editForm.nickname,
        gender: editForm.gender,
        bio: editForm.bio,
        avatar: avatarUrl
      })

      if (updateResult) {
        // 更新成功，更新本地状态
        setUserInfo(updateResult)
        setEditForm({
          nickname: updateResult.nickname,
          gender: updateResult.gender,
          bio: updateResult.bio || '',
          avatarFile: null,
          avatarPreview: null
        })
        setIsEditing(false)
        alert('保存成功')
      } else {
        alert('保存失败，请重试')
      }
    } catch (error) {
      console.error('保存编辑失败:', error)
      alert('保存失败，请重试')
    } finally {
      ToastUtils.showLoading(false)
    }
  }

  const handleLogout = async (): Promise<void> => {
    // 显示确认弹框
    const confirmed = window.confirm('确定要退出登录吗？');
    if (!confirmed) {
      return;
    }

    ToastUtils.showLoading(true);
    await userLogout();
    ToastUtils.showLoading(false);

    // 导航到启动页
    window.location.href = '/';
  }

  // 用户数据为空的情况
  if (!userInfo || !userInfo.nickname) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="text-center text-gray-500">
            <p>无法加载用户信息</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5 p-6">
      {/* 顶部固定信息区域 - 头像和基本信息 */}
      <div className="bg-white rounded-lg p-6 mb-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* 头像 */}
            <div className="relative">
              <div
                className={`relative ${isEditing ? 'cursor-pointer group' : ''}`}
                onClick={handleAvatarClick}
              >
                <img
                  src={editForm.avatarPreview || userInfo.avatar}
                  alt={userInfo.nickname?.slice(0, 2)}
                  className="w-28 h-28 rounded-full object-cover"
                  onError={(e) => {
                    // 图片加载失败时显示默认头像
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent && !parent.querySelector('.avatar-fallback')) {
                      const fallback = document.createElement('div');
                      fallback.className = 'avatar-fallback w-20 h-20 rounded-full bg-blue-500 text-white flex items-center justify-center text-xl font-semibold';
                      fallback.textContent = userInfo.nickname?.slice(0, 2).toUpperCase() || 'U';
                      parent.appendChild(fallback);
                    }
                  }}
                />
                {/* 编辑模式下的遮罩层 */}
                {isEditing && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                )}
              </div>
            </div>

            {/* 固定信息 */}
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <h3 className="text-xl font-semibold text-gray-900">{userInfo.nickname}</h3>
                <button
                  onClick={handleShowQrCode}
                  className="flex items-center space-x-2 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                >
                  <img src={icQr} alt="My QR Code" className="w-4 h-4" />
                  <span className="text-sm text-black">我的二维码</span>
                </button>
              </div>

              {/* 只读信息 */}
              <div className="space-y-1 text-sm text-gray-500">
                <p>用户ID: {userInfo.handle_rand || ''}</p>
                {tabAuth.getOrganizationCode() && (
                  <p>组织代码: {tabAuth.getOrganizationCode()}</p>
                )}
                {userInfo.phone && (
                  <p>手机号: +{userInfo.countryCode} {userInfo.phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* 编辑按钮区域 */}
          <div className="flex space-x-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSaveEdit}
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white rounded-lg transition-colors"
                >
                  {loading ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <span className="text-sm font-bold">保存</span>
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={loading}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 text-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span className="text-sm font-bold">取消</span>
                </button>
              </>
            ) : (
              <button
                onClick={handleStartEdit}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                <span className="text-sm font-bold text-black">编辑</span>
              </button>
            )}
          </div>
        </div>

        {/* 错误信息显示 */}
        {currentError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">{currentError}</p>
              <button
                onClick={clearError}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 底部编辑区域 */}
      <div className="bg-white rounded-lg p-6 shadow-sm">
        <h4 className="text-lg font-semibold text-gray-900 mb-6">个人信息编辑</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* 昵称 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              昵称
            </label>
            <input
              type="text"
              value={editForm.nickname}
              onChange={(e) => updateEditForm({ nickname: e.target.value })}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm ${isEditing
                ? 'focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                : 'bg-gray-50 text-gray-500'
                }`}
              readOnly={!isEditing}
              placeholder="输入昵称"
            />
          </div>

          {/* 性别 */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              性别
            </label>
            <div className="relative">
              <select
                value={editForm.gender === 'male' ? 'male' : editForm.gender === 'female' ? 'female' : 'unknown'}
                onChange={(e) => {
                  const value = e.target.value
                  const gender = value === 'male' ? 'male' : value === 'female' ? 'female' : 'unknown'
                  updateEditForm({ gender })
                }}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm appearance-none ${isEditing
                  ? 'focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                  : 'bg-gray-50 text-gray-500'
                  }`}
                disabled={!isEditing}
              >
                <option value="male">男</option>
                <option value="female">女</option>
                <option value="unknown">未知</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          {/* 个人简介 - 跨两列 */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
            个人简介
            </label>
            <textarea
              value={editForm.bio}
              onChange={(e) => updateEditForm({ bio: e.target.value })}
              className={`w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm resize-none ${isEditing
                ? 'focus:outline-none focus:ring-blue-500 focus:border-blue-500'
                : 'bg-gray-50 text-gray-500'
                }`}
              readOnly={!isEditing}
              placeholder="输入个人简介"
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* 退出登录 */}
      <div className="mt-2 bg-white rounded-lg p-4 shadow-sm cursor-pointer" onClick={handleLogout}>
        <div className="flex items-center justify-center">
          <button
            className="px-8 py-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
          >
            退出登录
          </button>
        </div>
      </div>

      {/* 二维码弹框 */}
      {
        showQrDialog && (
          <QrCodeDialog
            isOpen={showQrDialog}
            onClose={handleCloseQrDialog}
          />
        )
      }

    </div>
  )
}